import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { AnimatedIcon } from "./AnimatedIcon";

interface AIInsightCardProps {
  title: string;
  content: string | string[];
  icon?: React.ReactNode;
  variant?: "default" | "highlight" | "quote";
  collapsible?: boolean;
  className?: string;
}

export const AIInsightCard = ({ 
  title, 
  content, 
  icon,
  variant = "default",
  collapsible = false,
  className 
}: AIInsightCardProps) => {
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  const renderContent = () => {
    if (Array.isArray(content)) {
      return (
        <ul className="space-y-3">
          {content.map((item, index) => (
            <li 
              key={index} 
              className="flex items-start gap-3 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span className="text-primary mt-1 flex-shrink-0">→</span>
              <span className="text-foreground leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    
    return <p className="text-foreground leading-relaxed">{content}</p>;
  };

  const variantStyles = {
    default: "border-border bg-card",
    highlight: "border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 shadow-glow",
    quote: "border-l-4 border-l-primary bg-muted/50"
  };

  return (
    <Card className={cn(
      "overflow-hidden transition-smooth hover:shadow-lg animate-fade-in",
      variantStyles[variant],
      className
    )}>
      <CardHeader 
        className={cn(
          "cursor-pointer",
          collapsible && "hover:bg-muted/50 transition-smooth"
        )}
        onClick={() => collapsible && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon || <AnimatedIcon icon={Sparkles} className="text-primary" animation="pulse" />}
            <CardTitle className="text-xl bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              {title}
            </CardTitle>
          </div>
          {collapsible && (
            <div className="text-muted-foreground transition-transform">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="animate-fade-in">
          {renderContent()}
        </CardContent>
      )}
    </Card>
  );
};
